import { type UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useCanModifyUsers } from "../../../../hooks/useCanModifyUsers";
import { backendService } from "../../../../services/backend.service";
import type { ExistingAdminUser } from "../../../../utils/models";
import type { AdminRole, AdminRoleDefaults, AdminUser, UpdateAdminUsersResponse } from "./types";

/**
 * The role → permissions matrix only changes when someone edits `admin_role_defaults` in SQL, so it
 * is cached for the session rather than refetched per mount. A deploy is not required to pick up a
 * change — the next full load is enough.
 */
const ADMIN_ROLE_DEFAULTS_STALE_TIME_MS = 60 * 60 * 1000;

export function useAdminRoleDefaults(): UseQueryResult<AdminRoleDefaults, Error> {
	const {
		chosenDepartment: { department_id },
	} = useHospitalData();

	return useQuery<AdminRoleDefaults, Error>({
		// Keyed by department because the endpoint is department-gated, even though the matrix it
		// returns is the same everywhere — a cache shared across departments would be a scope lie.
		queryKey: ["useAdminRoleDefaults", department_id],
		queryFn: async () => {
			try {
				const response = await backendService.fetchAdminRoleDefaults(department_id);
				if (response.status >= 400) {
					throw Error(response.statusText);
				}

				return response.data;
			} catch (e) {
				throw Error("Could not fetch the admin role defaults", { cause: e });
			}
		},
		staleTime: ADMIN_ROLE_DEFAULTS_STALE_TIME_MS,
	});
}

/**
 * Saves one user's roles. Separate from `useUpdateAdminUsers` because roles have their own endpoint,
 * so a save is two calls — checkboxes, then roles (see `useTeamAdmins.performSave`).
 */
export function useSetAdminUserRoles() {
	const {
		chosenDepartment: { department_id },
	} = useHospitalData();
	const hasModifyUserPermission = useCanModifyUsers();

	return useMutation<void, Error, { userId: string; roles: AdminRole[] }>({
		mutationFn: async ({ userId, roles }) => {
			if (!hasModifyUserPermission) {
				throw Error("Current user is not authorized to update admin roles");
			}

			try {
				const response = await backendService.setAdminUserRoles(department_id, userId, roles);
				if (response.status >= 400) {
					throw Error(response.statusText);
				}
			} catch (e) {
				throw Error("Could not update admin roles", { cause: e });
			}
		},
	});
}

/** The list query's key. Exported so a mid-save `fetchQuery` writes to the same cache entry. */
export const adminUsersQueryKey = (department_id: string, userId: string) => ["useAdminUsers", department_id, userId] as const;

/**
 * The one way to canonicalize an admin's username before storing or matching it.
 *
 * `user.username` is UNIQUE, so `Foo@X.com ` and `foo@x.com` must not become two accounts. The
 * roles pass also looks new users up by this form, so skipping it loses their roles.
 */
export const canonicalUsername = (username: string | undefined): string => (username ?? "").trim().toLowerCase();

/** The list query's fetcher, shared so a mid-save re-read cannot drift from what the query returns. */
export const fetchAdminUsersList = async (department_id: string): Promise<AdminUser[]> => {
	try {
		const response = await backendService.fetchAdminUsers(department_id);
		if (response.status >= 400) {
			throw Error(response.statusText);
		}

		// DEFAULT: [] -- the field is always sent, but during a deploy the frontend can be newer
		// than the backend. Normalizing here lets every consumer treat it as a real list.
		return response.data.map((user) => ({ ...user, adminRoles: user.adminRoles ?? [] }));
	} catch (e) {
		throw Error("Could not fetch the department's admin users", { cause: e });
	}
};

export function useAdminUsers(): UseQueryResult<AdminUser[], Error> {
	const {
		chosenDepartment: { department_id },
	} = useHospitalData();
	const { userInfo } = useAuth();
	const hasModifyUserPermission = useCanModifyUsers();

	return useQuery<AdminUser[], Error>({
		queryKey: adminUsersQueryKey(department_id, userInfo.id),
		queryFn: async () => {
			if (!hasModifyUserPermission) {
				throw Error("Current user is not authorized to view admin users");
			}

			return fetchAdminUsersList(department_id);
		},
		enabled: hasModifyUserPermission,
	});
}

export function useAvailableAdminUsers({ specificDepartmentIds }: { specificDepartmentIds?: string[] }): UseQueryResult<ExistingAdminUser[], Error> {
	const { userInfo } = useAuth();
	const hasModifyUserPermission = useCanModifyUsers();

	const departmentIds = useMemo(
		() => (specificDepartmentIds || userInfo.departments.map((dept) => dept.department_id)).sort((a, b) => a.localeCompare(b)),
		[specificDepartmentIds, userInfo.departments],
	);

	return useQuery<ExistingAdminUser[], Error>({
		queryKey: ["useAvailableAdminUsers", ...departmentIds, userInfo.id],
		queryFn: async () => {
			if (!hasModifyUserPermission) {
				throw Error("Current user is not authorized to view admin users");
			}

			try {
				const response = await backendService.getAvailableAdminsAccordingToDepartments(departmentIds);
				if (response.status >= 400) {
					throw Error(response.statusText);
				}

				return response.data;
			} catch (e) {
				throw Error("Could not fetch the department's admin users", {
					cause: e,
				});
			}
		},
		enabled: hasModifyUserPermission,
	});
}

export function useUpdateAdminUsers({ newUsers, existingUsers }: { newUsers: AdminUser[]; existingUsers: AdminUser[] }) {
	const {
		chosenDepartment: { department_id },
	} = useHospitalData();
	const hasModifyUserPermission = useCanModifyUsers();

	return useMutation<UpdateAdminUsersResponse, Error>({
		mutationFn: async () => {
			if (!hasModifyUserPermission) {
				throw Error("Current user is not authorized to update admin users");
			}

			try {
				// `adminRoles` is stripped on purpose. This endpoint writes every field it is handed to the
				// `user` table, which has no such column, so sending it fails the whole save.
				const response = await backendService.updateAdminUsers(department_id, {
					new: newUsers.map(({ adminRoles: _adminRoles, ...user }) => ({
						...user,
						username: canonicalUsername(user.username),
						phoneNumber: user.phoneNumber?.trim() || undefined,
					})),
					existing: existingUsers.map(({ adminRoles: _adminRoles, ...user }) => ({
						...user,
						username: canonicalUsername(user.username),
						phoneNumber: user.phoneNumber?.trim() || undefined,
					})),
				});
				if (response.status >= 400) {
					throw Error(response.statusText);
				}

				return response.data;
			} catch (e) {
				throw Error("Could not update admin user data", { cause: e });
			}
		},
	});
}

export function useSendPasswordResetNotification({ userId }: { userId: string | undefined }) {
	return useMutation({
		mutationFn: async (channel: "email" | "sms") => {
			if (!userId) {
				throw Error("No user id");
			}

			try {
				const response = await backendService.sendWelcomeEmail({
					userId,
					channel,
				});
				if (response.status >= 400) {
					throw Error(response.statusText);
				}
			} catch (e) {
				throw Error("Could not send new welcome email", { cause: e });
			}
		},
	});
}

export function useRemoveAdminUserFromDepartment() {
	const {
		chosenDepartment: { department_id },
	} = useHospitalData();
	const hasModifyUserPermission = useCanModifyUsers();

	return useMutation<void, Error, string>({
		mutationFn: async (user_id: string) => {
			if (!hasModifyUserPermission) {
				throw Error("Current user is not authorized to remove admin users");
			}

			try {
				const response = await backendService.removeAdminUserFromDepartment(department_id, user_id);
				if (response.status >= 400) {
					throw Error(response.statusText);
				}
			} catch (e) {
				throw Error("Could not remove admin user from department", { cause: e });
			}
		},
	});
}
